/**
 * High-level OIDC + PKCE flow primitives. Coordinates `pkce.ts` (crypto),
 * `tokens.ts` (storage helpers + JWT decode), and `state.ts` (state machine).
 *
 * Per ADR-0021 + readiness Q-2 IAM contract, the SPA never holds the IAM
 * client secret: the browser POSTs `{code, code_verifier, redirect_uri}`
 * to the backend, which forwards to IAM `/connect/token` with the secret.
 */

import { config } from "@/config";
import { generateCodeChallenge, generateCodeVerifier, generateState } from "@/auth/pkce";
import {
  getSnapshot,
  markSessionEnding,
  setAuthenticated,
  setError,
  setExpired,
  setUnauthenticated,
  startAuthenticating,
  startRefreshing,
  startRestoring,
} from "@/auth/state";
import {
  clearPkceVerifier,
  clearSignedOut,
  markSignedOut,
  readPkceVerifier,
  storePkceVerifier,
  tokenSetFromResponse,
  wasSignedOut,
  type TokenSet,
} from "@/auth/tokens";

/** Build the IAM authorize URL with PKCE + state, persist the verifier, return the URL. */
export async function buildLoginRedirect(next: string): Promise<string> {
  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);
  const state = generateState();
  // A login the user chose ends the signed-out state, so the guard stops holding them
  // on the signed-out page.
  clearSignedOut();
  storePkceVerifier(verifier, state, next);
  startAuthenticating();
  const params = new URLSearchParams({
    response_type: "code",
    client_id: config.iamClientId,
    redirect_uri: config.iamRedirectUri,
    code_challenge: challenge,
    code_challenge_method: "S256",
    state,
    scope: config.iamScope,
  });
  return `${config.iamAuthorizeUrl}?${params.toString()}`;
}

export interface CallbackResult {
  /** Path to land on after successful exchange (the `?next=` value, or `/`). */
  next: string;
}

/**
 * Handle the IAM redirect-back leg: validate `state`, POST to the backend
 * exchange endpoint, and transition the auth state machine. Throws on any
 * failure so the caller can render an error UI.
 */
export async function handleCallback(searchParams: URLSearchParams): Promise<CallbackResult> {
  const code = searchParams.get("code");
  const returnedState = searchParams.get("state");
  const idpError = searchParams.get("error");
  if (idpError !== null) {
    clearPkceVerifier();
    const message = `IAM returned error: ${idpError}`;
    setError(message);
    throw new Error(message);
  }
  if (code === null || returnedState === null) {
    clearPkceVerifier();
    const message = "Callback missing required `code` and/or `state` parameters.";
    setError(message);
    throw new Error(message);
  }
  const stored = readPkceVerifier();
  if (stored.verifier === null || stored.state === null) {
    const message = "No PKCE verifier in session — login was not initiated by this tab.";
    setError(message);
    throw new Error(message);
  }
  if (stored.state !== returnedState) {
    clearPkceVerifier();
    const message = "PKCE state mismatch — possible CSRF; aborting exchange.";
    setError(message);
    throw new Error(message);
  }
  startAuthenticating();
  const exchangeUrl = `${config.apiBaseUrl}/api/v1/auth/exchange`;
  let resp: Response;
  try {
    resp = await fetch(exchangeUrl, {
      method: "POST",
      // `credentials: "include"` so the browser stores the httpOnly refresh
      // cookie the BFF sets (Set-Cookie is ignored on a fetch without it).
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        code,
        code_verifier: stored.verifier,
        redirect_uri: config.iamRedirectUri,
      }),
    });
  } catch (e) {
    clearPkceVerifier();
    const message = `Token exchange request failed: ${(e as Error).message}`;
    setError(message);
    throw new Error(message);
  }
  if (!resp.ok) {
    clearPkceVerifier();
    const message = `Token exchange failed with status ${resp.status}`;
    setError(message);
    throw new Error(message);
  }
  let body: unknown;
  try {
    body = await resp.json();
  } catch {
    clearPkceVerifier();
    const message = "Token exchange response was not valid JSON.";
    setError(message);
    throw new Error(message);
  }
  const next = stored.next;
  clearPkceVerifier();
  try {
    const tokens = tokenSetFromResponse(body as Parameters<typeof tokenSetFromResponse>[0]);
    setAuthenticated(tokens);
  } catch (e) {
    const message = (e as Error).message;
    setError(message);
    throw new Error(message);
  }
  return { next };
}

/**
 * Silent refresh. The refresh token lives in an httpOnly cookie the JS cannot read, so
 * every variant POSTs to the BFF with `credentials: "include"` and no body, and the
 * browser attaches the cookie. Three callers, three ways to handle a refusal:
 *
 * - `attemptRefresh()`: an API call already got a 401, so the token is dead. Failure →
 *   `expired`, and the caller sends the user to `/login`.
 * - `refreshInBackground()`: the provider's timers, while the token still works. Nothing
 *   unmounts, and a refusal only flags `sessionEnding` so the user is warned before the
 *   token runs out (docs#239 finding 41).
 * - `restoreSession()`: a full page load, which holds no token. Success means no IAM
 *   round trip and no `/auth/callback`. Failure falls back to the normal sign-in.
 */
let inFlightRefresh: Promise<RefreshResult> | null = null;

type RefreshResult = { ok: true; tokens: TokenSet } | { ok: false };

const REFRESH_LOCK = "sr.onboarding.auth.refresh";

/**
 * One refresh at a time, in this tab and across tabs.
 *
 * The refresh token is ROTATED on use and IAM allows no reuse, so two refreshes that send
 * the same cookie race: the second presents a consumed token and is refused. Within a tab
 * one in-flight promise is shared (every multi-query screen fires several requests at the
 * expiry boundary). Across tabs the Web Locks API serialises them, so the second tab sends
 * the cookie the first one's response just rotated. Browsers without Web Locks fall back
 * to the in-tab guard alone, which is what this code did before.
 */
function sharedRefresh(): Promise<RefreshResult> {
  if (inFlightRefresh) return inFlightRefresh;
  const run = (): Promise<RefreshResult> => requestRefresh();
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  // `locks.request` resolves with the callback's own resolved value. The DOM typings model
  // an async callback as a nested promise, which it never is at runtime.
  const pending: Promise<RefreshResult> =
    locks !== undefined && typeof locks.request === "function"
      ? (locks.request(REFRESH_LOCK, run) as unknown as Promise<RefreshResult>)
      : run();
  const shared = pending.finally(() => {
    inFlightRefresh = null;
  });
  inFlightRefresh = shared;
  return shared;
}

/**
 * Give up on a refresh after this long. A page load renders nothing while it restores, so
 * a stalled BFF must fall back to the normal sign-in rather than leave a blank page, and a
 * stalled refresh must not hold the cross-tab lock.
 *
 * Longer than the BFF's own IAM timeout (`_IAM_TIMEOUT_SEC`, 10 s, onboarding-api
 * api/auth.py). If the browser gave up first, a refresh IAM answered at 9.5 s would be
 * dropped with its rotated token, and IAM has already spent the old one.
 */
export const REFRESH_TIMEOUT_MS = 15_000;

async function requestRefresh(): Promise<RefreshResult> {
  const abort = new AbortController();
  const timer = setTimeout(() => {
    abort.abort();
  }, REFRESH_TIMEOUT_MS);
  try {
    const resp = await fetch(`${config.apiBaseUrl}/api/v1/auth/refresh`, {
      method: "POST",
      credentials: "include",
      signal: abort.signal,
    });
    if (!resp.ok) {
      return { ok: false };
    }
    const body = (await resp.json()) as Parameters<typeof tokenSetFromResponse>[0];
    const tokens = tokenSetFromResponse(body);
    // A refresh grant need not carry a new ID token. Keep the one from the exchange, so
    // sign out can still send IAM its id_token_hint after the access token has rotated.
    const previousIdToken = getSnapshot().tokens?.idToken;
    if (tokens.idToken === undefined && previousIdToken !== undefined) {
      tokens.idToken = previousIdToken;
    }
    return { ok: true, tokens };
  } catch {
    return { ok: false };
  } finally {
    clearTimeout(timer);
  }
}

/** Refresh after a 401. Returns true on success, else moves to `expired`. */
export async function attemptRefresh(): Promise<boolean> {
  startRefreshing();
  const result = await sharedRefresh();
  if (result.ok) {
    setAuthenticated(result.tokens);
    return true;
  }
  setExpired();
  return false;
}

/**
 * Refresh while the access token still works. The status stays `authenticated`
 * throughout, so the page does not unmount. On a refusal the session is flagged as ending
 * and the token is kept until it expires, so the user can finish or sign in again first.
 * A token that has already expired is handled like `attemptRefresh`.
 */
export async function refreshInBackground(): Promise<boolean> {
  if (getSnapshot().status !== "authenticated") return false;
  const result = await sharedRefresh();
  const snapshot = getSnapshot();
  // Signed out, or another path took over while the request was in flight.
  if (snapshot.status !== "authenticated" && snapshot.status !== "refreshing") return false;
  if (result.ok) {
    setAuthenticated(result.tokens);
    return true;
  }
  const expiresAtSec = snapshot.tokens?.expiresAtSec ?? 0;
  if (expiresAtSec <= Math.floor(Date.now() / 1000)) {
    setExpired();
    return false;
  }
  markSessionEnding();
  return false;
}

/** Paths a page load must not restore on: they run their own auth step. */
const NO_RESTORE_PATHS = ["/auth/callback", "/login", "/logout", "/signed-out"];

let restoreAttempted = false;

/**
 * On a full page load, try the refresh cookie before any guard sends the browser to IAM.
 *
 * Without this every reload, new tab or typed address went through IAM and
 * `/auth/callback` again, and that silent round trip only works while IAM's own browser
 * session is alive. Once it lapsed, the next page load landed on the IAM sign-in page with
 * no warning, mid-work (docs#239 finding 41).
 *
 * Runs once per page load and only from `unauthenticated`. Skipped after a sign out (the
 * cookie was cleared and the user chose the signed-out page) and on the routes that run
 * their own auth step. On a refusal the state returns to `unauthenticated`, so the route
 * guard redirects to `/login` with the full path and query, exactly as before.
 *
 * `startRestoring()` runs synchronously, before the first await, so a caller that renders
 * straight after calling this sees `refreshing` and no guard redirects meanwhile.
 */
export async function restoreSession(pathname: string): Promise<boolean> {
  if (restoreAttempted) return false;
  restoreAttempted = true;
  if (getSnapshot().status !== "unauthenticated") return false;
  if (wasSignedOut()) return false;
  if (NO_RESTORE_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`))) {
    return false;
  }
  startRestoring();
  const result = await sharedRefresh();
  // A sign-in or sign-out started meanwhile owns the state now.
  if (getSnapshot().status !== "refreshing") return false;
  if (result.ok) {
    setAuthenticated(result.tokens);
    return true;
  }
  setUnauthenticated();
  return false;
}

/** Test seam: let `restoreSession` run again and drop any shared refresh. */
export function _resetFlowForTests(): void {
  restoreAttempted = false;
  inFlightRefresh = null;
}

/**
 * Sign out: clear in-memory + sessionStorage state, drop the httpOnly refresh cookie,
 * and return the IAM end-session URL.
 *
 * The URL carries `id_token_hint`, the ID token from the code exchange. Without it IAM
 * kept its own session, so the next sign-in was silent and a shared lab computer let the
 * next person in as the previous doctor (docs#15 note 16577). Sign out from the header,
 * the `/logout` route and the idle timeout all come through here.
 *
 * `post_logout_redirect_uri` is the origin with a trailing slash, the root URL as IAM
 * registers it. Measured on alpha 2026-11-02 (docs#239 note 19795 item 42): its
 * `/connect/logout` answers `https://sr-portal-alpha.medicus.ai/` with a 302 and the bare
 * origin with 400 ID2052 "post_logout_redirect_uri is invalid", so the bare origin this
 * used to send would have failed as soon as the path was right. The signed-out page is
 * reached through a sessionStorage marker read by the route guard, so it needs no new URI
 * registered on the IAM client.
 */
export function buildLogoutRedirect(): string {
  // Read before clearing: setUnauthenticated() drops the tokens.
  const idToken = getSnapshot().tokens?.idToken;
  setUnauthenticated();
  clearPkceVerifier();
  markSignedOut();
  // Tell the BFF to expire the refresh cookie. `keepalive` lets the request finish
  // after the page navigates to IAM, which a plain fetch is not guaranteed to do.
  if (typeof fetch === "function") {
    void fetch(`${config.apiBaseUrl}/api/v1/auth/logout`, {
      method: "POST",
      credentials: "include",
      keepalive: true,
    }).catch(() => {
      /* logout is advisory; IAM end-session is the authoritative sign-out */
    });
  }
  const params = new URLSearchParams({
    client_id: config.iamClientId,
    post_logout_redirect_uri: typeof window !== "undefined" ? `${window.location.origin}/` : "",
  });
  if (idToken !== undefined) {
    params.set("id_token_hint", idToken);
  }
  return `${config.iamEndSessionUrl}?${params.toString()}`;
}
